import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ScreenSizeDetectorService } from '@serviceplan/ui-kit';
import { NavigationItem, NavigationItemsService, NavigationViewEntry } from '@thehubz/client/core';
import { AnyType } from '@thehubz/shared/model';

@Component({
  selector: 'thehubz-main-nav',
  templateUrl: './main-nav.component.html',
  styleUrls: ['./main-nav.component.scss'],
})
export class MainNavComponent implements OnInit {
  @Input() isMobile = false;
  @Input() isMobileNavVisible = false;
  @Output() hideMainNav = new EventEmitter<string>();

  navEntries: NavigationViewEntry[] = [];
  currentURL = '';
  pointerTimeout: AnyType;
  pointerTimeoutDelay = 300;
  canHover = window.matchMedia('(hover: hover)').matches;

  private activeNavEntry?: NavigationViewEntry;
  private activeNavEntryQueue: NavigationViewEntry[] = [];

  constructor(
    private readonly router: Router,
    private readonly navigationItemService: NavigationItemsService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {}

  ngOnInit(): void {
    this.navEntries = this.navigationItemService.getNavigation();

    if (!this.canHover) {
      // A small delay is still necessary for the sub level links to navigate to its destination,
      // otherwise the dropdown will disappear before the click gets handled.
      this.pointerTimeoutDelay = 50;
    }

    this.checkRoute();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.hideNav();
        this.checkRoute();
        if (this.screenSizeDetectorService.isSmallScreen || !this.canHover) this.resetActiveEntry();
      }
    });
  }

  showNav(): void {
    const mainMenu: HTMLElement | null = document.querySelector('#main-menu');
    if (mainMenu) mainMenu.focus();
  }

  hideNav(): void {
    this.hideMainNav.emit();

    const mainMenuShowButton: HTMLButtonElement | null = document.querySelector('#main-nav-show-button');
    if (mainMenuShowButton) mainMenuShowButton.focus();
  }

  checkRoute() {
    this.currentURL = this.router.url;

    this.navEntries.forEach((navEntry: NavigationViewEntry) => {
      if (this.screenSizeDetectorService.isSmallScreen && this.getHasCurrent(navEntry)) {
        this.showChildren(navEntry);
      }
    });
  }

  getHasCurrent(navEntry: NavigationViewEntry) {
    let result = false;

    const currentUri = this.currentURL.split('?')[0];

    if (navEntry.hasCurrent) return true;

    navEntry.items.forEach((item: NavigationItem) => {
      const itemUrl = '/' + item.routerLink;
      const subItemUrlRegex = new RegExp(`^${itemUrl}/`);

      if (subItemUrlRegex.test(currentUri)) {
        result = true;
      }
    });

    return result;
  }

  getIsCurrent(item: NavigationItem) {
    let result = false;

    const currentUri = this.currentURL.split('?')[0];
    const itemUrl = '/' + item.routerLink;

    if (currentUri === itemUrl || (/collections/.test(itemUrl) && /collections/.test(currentUri))) {
      result = true;
    }

    return result;
  }

  toggleChildren(navEntry: NavigationViewEntry) {
    if (navEntry.isExpanded) {
      this.hideChildren(navEntry);
    } else {
      this.showChildren(navEntry);
    }
  }

  showChildren(navEntry: NavigationViewEntry) {
    this.navEntries.forEach((entry: NavigationViewEntry) => {
      entry.isExpanded = navEntry === entry;
    });
  }

  hideChildren(navEntry: NavigationViewEntry) {
    navEntry.isExpanded = false;
  }

  setActiveEntry() {
    const newActive = this.activeNavEntryQueue.pop();
    const prevActive = this.activeNavEntry;

    this.activeNavEntry = newActive;
    this.activeNavEntryQueue = [];

    if (newActive === prevActive) return;

    if (newActive) {
      this.showChildren(newActive);
    }

    if (prevActive) {
      this.hideChildren(prevActive);
    }
  }

  resetActiveEntry() {
    if (this.pointerTimeout) {
      clearTimeout(this.pointerTimeout);
      this.pointerTimeout = null;
    }

    if (this.activeNavEntry) {
      this.hideChildren(this.activeNavEntry);
    }

    this.activeNavEntry = undefined;
    this.activeNavEntryQueue = [];
  }

  requestActiveEntryUpdate() {
    if (this.pointerTimeout) {
      clearTimeout(this.pointerTimeout);
      this.pointerTimeout = null;
    }

    this.pointerTimeout = setTimeout(() => {
      this.setActiveEntry();
    }, this.pointerTimeoutDelay);
  }

  @HostListener('keydown.escape ', ['$event'])
  onHideMainNav(): void {
    this.hideNav();
  }

  @HostListener('body:click', ['$event'])
  onOutsideClick(event: AnyType): void {
    if (event?.target && !event.target.closest('#main-nav')) {
      this.resetActiveEntry();
    }
  }

  onToggleChildren(navEntry: NavigationViewEntry, event: AnyType) {
    event?.preventDefault();
    if (navEntry.isExpanded) {
      this.onHideChildren(navEntry, event);
    } else {
      this.onShowChildren(navEntry, event);
    }
  }

  onShowChildren(navEntry: NavigationViewEntry, event: AnyType) {
    event?.preventDefault();
    this.activeNavEntryQueue.push(navEntry);
    this.requestActiveEntryUpdate();
  }

  onHideChildren(navEntry: NavigationViewEntry, event: AnyType) {
    event?.preventDefault();
    this.activeNavEntryQueue = this.activeNavEntryQueue.filter((entry) => entry !== navEntry);
    this.requestActiveEntryUpdate();
  }
}
